import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Search, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { searchClaimableProviders } from "@/server/claim.functions";
import { brandedTitle, safeDesc } from "@/lib/seo-meta";

const SITE = "https://heallexa.com";

const META_TITLE = brandedTitle("List Your Practice | Free & Premium Plans");
const META_DESC = safeDesc(
  "Get your practice discovered on Heallexa. Free and Premium listing plans for doctors, dentists, therapists, and clinics. Claim your profile and accept appointment requests."
);

export const Route = createFileRoute("/list-your-practice")({
 head: () => ({
 meta: [
 { title: META_TITLE },
 { name: "description", content: META_DESC },
 { property: "og:title", content: META_TITLE },
 { property: "og:description", content: META_DESC },
 { property: "og:url", content: `${SITE}/list-your-practice` },
 { property: "og:type", content: "website" },
 { name: "twitter:title", content: META_TITLE },
 { name: "twitter:description", content: META_DESC },
 ],
 links: [{ rel: "canonical", href: `${SITE}/list-your-practice` }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "HowTo",
 name: "How to List Your Practice on Heallexa",
 description: "Claim or add your healthcare practice on Heallexa in minutes. Choose a Free or Premium plan and start receiving appointment requests.",
 totalTime: "PT5M",
 step: [
 {
 "@type": "HowToStep",
 position: 1,
 name: "Search for your practice",
 text: "Enter your practice name, NPI number, license number, city, or address in the search bar to find your existing Heallexa listing.",
 },
 {
 "@type": "HowToStep",
 position: 2,
 name: "Verify ownership",
 text: "Confirm you are the owner of the practice by verifying with your email address or practice credentials.",
 },
 {
 "@type": "HowToStep",
 position: 3,
 name: "Edit your listing",
 text: "Update your practice details including specialties, accepted insurance, services, languages spoken, photos, and appointment availability.",
 },
 {
 "@type": "HowToStep",
 position: 4,
 name: "Choose plan & go live",
 text: "Select a Free listing or upgrade to Premium starting at $99/month for priority placement, a verified badge, online booking, and performance analytics.",
 },
 ],
 }),
 },
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Home", item: SITE },
 { "@type": "ListItem", position: 2, name: "List Your Practice", item: `${SITE}/list-your-practice` },
 ],
 }),
 },
 ],
 }),
 component: ListPage,
});

function ListPage() {
 const { t } = useI18n();
 const navigate = useNavigate();
 const [billing, setBilling] = useState<"monthly" | "yearly">("yearly");
 const yearlySavings = 99 * 12 - 999;
 const yearlyDiscountPct = Math.round((yearlySavings / (99 * 12)) * 100);
 const plansRef = useRef<HTMLDivElement | null>(null);

 const runSearch = useServerFn(searchClaimableProviders);
 const [q, setQ] = useState("");
 const [searching, setSearching] = useState(false);
 const [results, setResults] = useState<any[] | null>(null);
 const [searched, setSearched] = useState(false);

 useEffect(() => {
   const term = q.trim();
   if (term.length < 2) { setResults(null); setSearched(false); return; }
   setSearching(true);
   const handle = setTimeout(async () => {
     try {
       const res = await runSearch({ data: { q: term } });
       setResults(res.results);
       setSearched(true);
     } catch { setResults([]); setSearched(true); }
     finally { setSearching(false); }
   }, 350);
   return () => clearTimeout(handle);
 }, [q, runSearch]);

 const scrollToPlans = () => plansRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

 return (
 <div className="min-h-screen bg-background pb-1">
 <Navbar />

 <section className="pt-24 border-b border-border bg-card" />


 <section className="border-b border-border bg-background">
 <div className="mx-auto max-w-3xl px-6 py-10">
 <div className="text-center mb-5">
 <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider uppercase text-primary border border-primary/30 px-3 py-1 mb-3">
 <ShieldCheck className="h-3 w-3" /> Already listed?
 </span>
 <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">Find your profile to claim it</h2>
 <p className="mt-2 text-sm text-muted-foreground">Search by name, practice, NPI, license number, city, or address.</p>
 </div>
 <div className="relative">
 <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
 <input
 type="search"
 value={q}
 onChange={(e) => setQ(e.target.value)}
 placeholder="e.g. Jane Smith MD, 1234567890, San Diego"
 className="w-full min-h-14 border border-border bg-card pl-11 pr-4 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
 aria-label="Search for your provider profile"
 />
 {searching && (
 <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
 )}
 </div>

 {results && results.length > 0 && (
 <ul className="mt-4 border border-border bg-card divide-y divide-border">
 {results.map((p) => {
   const claimed = !!p.claimed_by;
   return (
     <li key={p.id} className="flex items-center gap-3 p-4">
       <div className="flex-1 min-w-0">
         <div className="text-sm font-semibold text-foreground truncate">
           {p.practice || p.name}
         </div>
         <div className="text-xs text-muted-foreground truncate">
           {[p.name !== p.practice ? p.name : null, p.specialty, [p.city, p.state].filter(Boolean).join(", "), p.npi ? `NPI ${p.npi}` : null]
             .filter(Boolean).join(" · ")}
         </div>
       </div>
       {claimed ? (
         <span className="inline-flex items-center gap-1 text-xs text-muted-foreground border border-border px-2 py-1">
           <CheckCircle2 className="h-3 w-3" /> Already claimed
         </span>
       ) : (
         <Button asChild size="sm">
           <Link to="/claim" search={{ providerId: p.id, plan: "free" } as any}>Claim this</Link>
         </Button>
       )}
     </li>
   );
 })}
 </ul>
 )}

 {searched && results && results.length === 0 && (
 <div className="mt-4 border border-border bg-card p-5 text-center">
 <p className="text-sm text-foreground font-medium">No matching listing found.</p>
 <p className="mt-1 text-xs text-muted-foreground">Add your practice to Heallexa in under a minute.</p>
 <Button className="mt-4" onClick={scrollToPlans}>List my practice</Button>
 </div>
 )}

 <div className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
 <span>Don't see your practice?</span>
 <button type="button" onClick={scrollToPlans} className="text-primary font-medium hover:underline">
   Add a new listing →
 </button>
 </div>
 </div>
 </section>

 

 <section ref={plansRef} className="mx-auto max-w-6xl px-6 py-16">
 <div className="mx-auto mb-8 grid max-w-md grid-cols-2 gap-2" role="group" aria-label="Choose premium billing cycle">
 <button
 type="button"
 onClick={() => setBilling("monthly")}
 aria-pressed={billing === "monthly"}
 className={`min-h-14 border px-4 py-3 text-left transition-all ${
 billing === "monthly"
 ? "border-primary bg-primary text-primary-foreground shadow-sm"
 : "border-border bg-card text-foreground hover:border-primary/50"
 }`}
 >
 <span className="block text-sm font-semibold">Monthly</span>
 <span className={billing === "monthly" ? "block text-xs text-primary-foreground/80" : "block text-xs text-muted-foreground"}>$99/mo <span className="line-through opacity-70 ml-1">$149</span></span>
 </button>
 <button
 type="button"
 onClick={() => setBilling("yearly")}
 aria-pressed={billing === "yearly"}
 className={`min-h-14 border px-4 py-3 text-left transition-all ${
 billing === "yearly"
 ? "border-primary bg-primary text-primary-foreground shadow-sm"
 : "border-border bg-card text-foreground hover:border-primary/50"
 }`}
 >
 <span className="block text-sm font-semibold">Yearly</span>
 <span className={billing === "yearly" ? "block text-xs text-primary-foreground/80" : "block text-xs text-muted-foreground"}>Save {yearlyDiscountPct}% · $999/yr <span className="line-through opacity-70 ml-1">$1,490</span></span>
 </button>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
 <Plan
 name="Free Listing"
 price="$0"
 subtitle="Always free"
 features={["Practice name", "Phone number", "Address", "Website link", "Listed in Browse & Search"]}
 plan="free"
 ctaLabel="Claim free listing"
 ctaVariant="outline"
 />
 <Plan
 name="Premium Listing"
 price={billing === "yearly" ? "$83.25" : "$99"}
 strikePrice={billing === "yearly" ? "$1,490/yr" : "$149"}
 subtitle={billing === "yearly" ? `per month, billed $999/year, save $${yearlySavings}` : "per month, billed monthly"}
 features={[
 "Everything in Free",
 "Verified badge",
 "Priority placement in results",
 "Photos & logo",
 "List of services",
 "Insurance accepted",
 "Online booking link",
 "Performance analytics",
 "Featured 3 days, twice a month, in Top Rated Doctors in your city",
 ]}
 plan={billing === "yearly" ? "premium_yearly" : "premium_monthly"}
 ctaLabel="Start Premium"
 highlight
 />
 </div>
 </section>

 <section className="bg-foreground text-background py-16">
 <div className="mx-auto max-w-4xl px-6">
 <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-10 text-center">How claiming works</h2>
 <ol className="grid grid-cols-1 md:grid-cols-4 gap-6">
 {[
 "Search for your practice",
 "Verify ownership",
 "Edit your listing",
 "Choose plan & go live",
 ].map((step, i) => (
 <li key={step}>
 <span className="text-3xl font-bold text-background/30">{String(i + 1).padStart(2, "0")}</span>
 <p className="mt-2 text-sm">{step}</p>
 </li>
 ))}
 </ol>
 </div>
 </section>

 <FooterSection />
 </div>
 );
}

function Plan({ name, price, strikePrice, subtitle, features, plan, ctaLabel, ctaVariant, highlight }: {
 name: string; price: string; strikePrice?: string; subtitle: string; features: string[]; plan: string; ctaLabel: string; ctaVariant?: "default" | "outline"; highlight?: boolean;
}) {
 const navigate = useNavigate();
 const signupPlan = plan;
 const go = () => navigate({ to: "/signup", search: { plan: signupPlan } as any });
 return (
 <div
 role="button"
 tabIndex={0}
 onClick={go}
 onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } }}
 className={`cursor-pointer border bg-card p-8 transition-colors hover:border-primary/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${highlight ? "border-primary border-2 shadow-lg" : "border-border"}`}
 >
 <h3 className="text-sm font-semibold tracking-wider uppercase text-muted-foreground">{name}</h3>
 <div className="mt-3 flex items-baseline gap-2">
 <span className="text-5xl font-bold tracking-tight text-foreground">{price}</span>
 {strikePrice && <span className="text-lg text-muted-foreground line-through">{strikePrice}</span>}
 </div>
 <p className="text-sm text-muted-foreground">{subtitle}</p>
 <ul className="mt-6 space-y-2.5">
 {features.map((f) => (
 <li key={f} className="flex items-start gap-2 text-sm text-foreground">
 <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {f}
 </li>
 ))}
 </ul>
 <div className="mt-8">
 <Button variant={ctaVariant ?? "default"} className="w-full" onClick={(e) => { e.stopPropagation(); go(); }}>
 {ctaLabel}
 </Button>
 </div>
 </div>
 );
}
